import React from "react";
import { Text } from "@react-pdf/renderer";
import moment from "moment";
import { formatFieldValue } from "../../utils/date";
import styles from "./components/Styles";
import { Permit } from "../../models/Permit";
import Header from "./components/Header";
import Row from "./components/Row";
import Item from "./components/Item";
import HeaderText from "./components/HeaderText";

interface Props {
  permit: Permit;
  orgLogoUrl: string | null;
  token: string;
}

const PermitDescriptionPage = ({ permit, orgLogoUrl, token }: Props) => {
  return (
    <>
      <Header logoUrl={orgLogoUrl} token={token} title={permit.type.name} permitNumber={permit.number} />

      <HeaderText title="PROJECT" />

      <Row border>
        <Item title="Project name" text={permit.project.name} width="50%" />
        <Item title="Project no." text={permit.project.number} width="50%" />
      </Row>

      <HeaderText title="DETAILS" />
      <Row border>
        <Item title="Contractor" text={permit.contractor} width="50%" />
        <Item title="Sub-contractor" text={permit.subContractor} width="50%" />
      </Row>

      <Row border>
        <Item title="Works description" text={permit.worksDescription} width="50%" />
        <Item title="Location of work" text={permit.locationOfWork} width="50%" />
      </Row>

      <Row border>
        <Item
          title="Duration"
          text={`${moment.utc(permit.validityDuration.from).local().format("L")} to ${moment
            .utc(permit.validityDuration.to)
            .local()
            .format("L")}`}
          width="50%"
        />
        <Item
          title="Hours"
          text={`${String(moment.duration(permit.validityDuration?.hoursFrom).hours()).padStart(2, "0")}:${String(
            moment.duration(permit.validityDuration?.hoursFrom).minutes(),
          ).padStart(2, "0")} to ${String(moment.duration(permit.validityDuration?.hoursTo).hours()).padStart(
            2,
            "0",
          )}:${String(moment.duration(permit.validityDuration?.hoursTo).minutes()).padStart(2, "0")} ${permit.validityDuration?.endsNextDay ? "(next day)" : ""}`}
          width="50%"
        />
      </Row>

      {permit.template?.fields &&
        permit.template.fields.length > 0 &&
        [...permit.template.fields]
          .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
          .reduce<React.ReactNode[]>((rows, field, i, arr) => {
            if (i % 2 === 0) {
              const next = arr[i + 1];
              const fv = permit.fieldValues?.find((v) => v.fieldKey === field?.key);
              const nextFv = next ? permit.fieldValues?.find((v) => v.fieldKey === next?.key) : undefined;
              rows.push(
                <Row key={field?.key} border>
                  <Item title={field?.label ?? field?.key ?? ""} text={formatFieldValue(field, fv)} width="50%" />
                  <Item title={next?.label ?? next?.key ?? ""} text={formatFieldValue(next, nextFv)} width="50%" />
                </Row>,
              );
            }
            return rows;
          }, [])}

      {permit.template?.features?.catAndGenny?.enabled && (
        <>
          <Row border>
            <Item title="CAT serial number" text={permit.calibrationCertificate?.catSerialNumber} width="50%" />
            <Item
              title="CAT calibration date"
              text={
                (permit.calibrationCertificate?.catCalibrationDate
                  ? moment.utc(permit.calibrationCertificate.catCalibrationDate).local().format("L")
                  : "") +
                (permit.calibrationCertificate?.catCalibrationDueDate
                  ? " due " + moment.utc(permit.calibrationCertificate.catCalibrationDueDate).local().format("L")
                  : "")
              }
              width="50%"
            />
          </Row>

          <Row border>
            <Item title="Genny serial number" text={permit.calibrationCertificate?.gennySerialNumber} width="50%" />
            <Item
              title="Genny calibration date"
              text={
                (permit.calibrationCertificate?.gennyCalibrationDate
                  ? moment.utc(permit.calibrationCertificate.gennyCalibrationDate).local().format("L")
                  : "") +
                (permit.calibrationCertificate?.gennyCalibrationDueDate
                  ? " due " + moment.utc(permit.calibrationCertificate.gennyCalibrationDueDate).local().format("L")
                  : "")
              }
              width="50%"
            />
          </Row>
        </>
      )}

      {permit.template?.features?.affectedServices?.enabled && (
        <Row border>
          <Item title="Affected services" text={permit.affectedServices.join(", ")} width="100%" />
        </Row>
      )}

      <Row border last>
        <Item title="Comments" text={permit.comments} width="100%" />
      </Row>

      <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
    </>
  );
};

export default PermitDescriptionPage;
